import React from "react";
import { Box, IconButton, Typography } from "@mui/material";
import { TreeItem } from "@mui/x-tree-view";
import ArticleIcon from "@mui/icons-material/Article";
import CalculateIcon from "@mui/icons-material/Calculate";
import LibraryBooksIcon from "@mui/icons-material/LibraryBooks";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import { pvEstimationTabForDocument } from "../../../../../utils/pvEstimationDocs";
import { docLabel, isPvWbs, wbsLabel } from "../utils/treeLabels";
import { useTreeViewContext } from "../TreeViewContext";
import {
  estimationNodeId,
  inputDataNodeId,
  revisionDocNodeId,
  specNodeId,
} from "../utils/treeNodeIds";
import { AddActionIcon, AddActionLabel } from "./AddAction";

/**
 * "+ Add revision" - the tree's stand-in for the "+ Add New Revision"
 * option those modules used to offer in their header Rev. dropdown. Like
 * that option it only branches a revision; it is saved when the user saves
 * on the module page it opens.
 * Sits on a WBS's latest revision row, whichever module the WBS belongs to:
 * a new revision starts from that row's data. A WBS with no revisions yet
 * carries it on the WBS row itself, since there is no row to hang it on.
 */
export const AddRevisionButton = ({
  plant,
  wbs,
  revision,
  subTabLabel,
  topTabLabel,
  wbsType,
}) => {
  const { pickerMode, setConfirmTarget } = useTreeViewContext();
  if (pickerMode) return null;

  // PV needs the revision number up front (its page takes it from the URL),
  // and that can only be derived once the revision list has loaded.
  const disabled = revision === null;

  return (
    <IconButton
      size="small"
      disabled={disabled}
      title={
        disabled
          ? "Expand this WBS to load its revisions first"
          : "Add a new revision, starting from this one's data"
      }
      onClick={(event) => {
        event.stopPropagation();
        setConfirmTarget({
          kind: "new-revision",
          plantId: plant.id,
          projectId: wbs.id,
          projectName: wbsLabel(wbs),
          revision,
          subTabLabel,
          topTabLabel,
          wbsType,
        });
      }}
      sx={{ p: 0.25, borderRadius: 1 }}
    >
      <AddActionIcon color="warning.main" />
      <AddActionLabel>Add Rev</AddActionLabel>
    </IconButton>
  );
};

// One of the documents filed under a revision. Under a PV revision, a
// document that stands for one of that revision's Estimation tabs opens it -
// an Estimation Summary opens Main Estimation, a BOM opens Main BOM - so the
// same view is reached either from the tab itself or from the document filed
// under the revision it belongs to. Every other document is still a leaf: it
// carries no selection-target entry and node selection ignores it, since
// there is nothing to open.
export const RevisionDocNode = ({ plant, wbs, revisionEntry, doc }) => {
  const { nodeTargetRef } = useTreeViewContext();
  const id = revisionDocNodeId(wbs.id, revisionEntry.revision, doc?.id);
  const label = docLabel(doc);
  const estimationSubTabLabel = isPvWbs(wbs)
    ? pvEstimationTabForDocument(doc)
    : null;

  if (estimationSubTabLabel) {
    nodeTargetRef.current[id] = {
      kind: "pv",
      plantId: plant.id,
      projectId: wbs.id,
      projectName: wbsLabel(wbs),
      revision: revisionEntry.revision,
      subTabLabel: "Estimation",
      estimationSubTabLabel,
      // What the confirm dialog calls the destination - the tab this
      // document opens, rather than the "Estimation" that holds it.
      openLabel: estimationSubTabLabel,
    };
  } else {
    // The register is editable, so a document can stop naming a tab it
    // named before (its identifier changed). Targets are rebuilt on every
    // render but never cleared, so drop this one rather than leave the node
    // clicking through to where it used to lead.
    delete nodeTargetRef.current[id];
  }

  return (
    <TreeItem
      key={id}
      nodeId={id}
      icon={
        <ReceiptLongIcon fontSize="inherit" sx={{ color: "warning.main" }} />
      }
      label={
        <Typography
          variant="caption"
          fontWeight={600}
          color="text.secondary"
          title={label}
          sx={{ whiteSpace: "normal", wordBreak: "break-word" }}
        >
          {label}
        </Typography>
      }
    />
  );
};

// One PV revision: the Input Data document UID is the revision itself, so
// that revision's Estimation hangs underneath it rather than in a separate
// top-level folder - PV's Estimation is always of a specific input revision.
// `addRevisionAction` is passed only for the latest revision - a new one
// starts from that revision's data, so the button sits on the row it
// copies rather than up on the WBS.
export const PvInputNode = ({
  plant,
  wbs,
  revisionEntry,
  revisionDocs,
  addRevisionAction = null,
}) => {
  const { nodeTargetRef } = useTreeViewContext();
  const id = inputDataNodeId(wbs.id, revisionEntry.revision);
  nodeTargetRef.current[id] = {
    kind: "pv",
    plantId: plant.id,
    projectId: wbs.id,
    projectName: wbsLabel(wbs),
    revision: revisionEntry.revision,
    subTabLabel: "Input Data",
  };
  return (
    <TreeItem
      key={id}
      nodeId={id}
      className="branch-accent"
      icon={<ArticleIcon fontSize="inherit" sx={{ color: "warning.main" }} />}
      label={
        <Box
          sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}
        >
          <Typography
            variant="caption"
            title={revisionEntry.documentUID}
            sx={{
              flex: 1,
              minWidth: 0,
              whiteSpace: "normal",
              wordBreak: "break-word",
            }}
          >
            {revisionEntry.documentUID}
          </Typography>
          {addRevisionAction}
        </Box>
      }
    >
      {revisionDocs.map((doc) => (
        <RevisionDocNode
          key={doc?.id}
          plant={plant}
          wbs={wbs}
          revisionEntry={revisionEntry}
          doc={doc}
        />
      ))}
    </TreeItem>
  );
};

// Structure/Piping/Instrumentation's own Estimation - a revision inside the
// project record's estimationTable rather than one of PV's Input Data
// revisions, but named by the same Rev UID, written on save by
// savePipingData / saveStructuralData (see utils/revisionUID.js).
// Same as PvInputNode: only the latest revision carries the "Add Rev"
// button, since that is the revision a new one copies.
export const ProjectEstimationNode = ({
  plant,
  wbs,
  revisionEntry,
  topTabLabel,
  revisionDocs,
  addRevisionAction = null,
}) => {
  const { nodeTargetRef } = useTreeViewContext();
  const id = estimationNodeId(wbs.id, revisionEntry.revision);
  nodeTargetRef.current[id] = {
    kind: "pv",
    plantId: plant.id,
    projectId: wbs.id,
    projectName: wbsLabel(wbs),
    revision: revisionEntry.revision,
    wbsType: wbs.type,
    topTabLabel,
  };
  // className: the same accent line PV's revision rows draw, now that
  // these hang off the WBS the same way (see PvInputNode).
  return (
    <TreeItem
      key={id}
      nodeId={id}
      className="branch-accent"
      icon={
        <CalculateIcon fontSize="inherit" sx={{ color: "warning.main" }} />
      }
      label={
        <Box
          sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}
        >
          <Typography
            variant="caption"
            title={revisionEntry.documentUID}
            sx={{
              flex: 1,
              minWidth: 0,
              whiteSpace: "normal",
              wordBreak: "break-word",
            }}
          >
            {revisionEntry.documentUID || `Rev ${revisionEntry.revision}`}
          </Typography>
          {addRevisionAction}
        </Box>
      }
    >
      {revisionDocs.map((doc) => (
        <RevisionDocNode
          key={doc?.id}
          plant={plant}
          wbs={wbs}
          revisionEntry={revisionEntry}
          doc={doc}
        />
      ))}
    </TreeItem>
  );
};

// Piping's Spec, now a WBS of its own rather than a tab on the Piping WBS.
// A single leaf, not a folder of revisions: `selectedSpecs` is one flat list
// on the project, never keyed by revision the way estimationTable is.
//
// The Spec WBS is its own record holding its own specs, so it links to its
// own id - `wbsType` is what tells the module to open Spec, not Estimation.
export const SpecNode = ({ plant, wbs }) => {
  const { nodeTargetRef } = useTreeViewContext();
  const id = specNodeId(wbs.id);
  nodeTargetRef.current[id] = {
    kind: "pv",
    plantId: plant.id,
    projectId: wbs.id,
    projectName: wbsLabel(wbs),
    wbsType: wbs.type,
    topTabLabel: "Piping",
    // Names the tab in the confirm dialog only. Deliberately not
    // subTabLabel, which would also add a "pv-modeling" hash meant for PV.
    openLabel: "Spec",
  };
  return (
    <TreeItem
      key={id}
      nodeId={id}
      icon={
        <LibraryBooksIcon fontSize="inherit" sx={{ color: "warning.main" }} />
      }
      label={
        <Typography variant="caption" sx={{ whiteSpace: "normal" }}>
          Spec
        </Typography>
      }
    />
  );
};
